import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CONFIG } from './config';
import Header from './components/Header';
import Hero from './components/Hero';
import Problem from './components/Problem';
import USP from './components/USP';
import SamplePages from './components/SamplePages';
import SuccessStories from './components/SuccessStories';
import Reviews from './components/Reviews';
import Coverage from './components/Coverage';
import FAQ from './components/FAQ';
import Process from './components/Process';
import QuoteForm from './components/QuoteForm';
import DemoPreviewModal from './components/DemoPreviewModal';
import './App.css';

const PREVIEW_MODE = import.meta.env.VITE_PREVIEW_MODE === "true";

function App() {
  const [activeDemo, setActiveDemo] = useState(null);

  useEffect(() => {
    document.title = CONFIG.SEO.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', CONFIG.SEO.description);
    
    // Disable indexing if in preview mode
    if (PREVIEW_MODE) {
      const robots = document.querySelector('meta[name="robots"]');
      if (robots) robots.setAttribute('content', 'noindex,nofollow');
    }
  }, []);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": CONFIG.BRAND_NAME,
    "url": "https://bpasolutions.au",
    "logo": "https://greta-preview.s3.us-east-2.amazonaws.com/assets/logo.svg",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": CONFIG.SUPPORT_PHONE,
      "contactType": "customer service",
      "email": CONFIG.SUPPORT_EMAIL,
      "areaServed": "AU",
      "availableLanguage": "English"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": CONFIG.BUSINESS_ADDRESS,
      "addressLocality": "Surry Hills",
      "addressRegion": "NSW",
      "postalCode": "2010",
      "addressCountry": "AU"
    }
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden selection:bg-blue-500/30 selection:text-white">
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      <div className="noise-overlay" />

      {/* Preview Banner Only If Enabled */}
      {PREVIEW_MODE && (
        <div className="fixed top-0 left-0 right-0 bg-blue-600/90 backdrop-blur-md text-white text-[10px] uppercase tracking-[0.3em] text-center py-1.5 z-[1000] font-black border-b border-white/10">
          Internal Preview Development Only &bull; Do Not Share
        </div>
      )}

      <Header />

      <div className="fixed inset-0 z-[-1] bg-[#080a0d]">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-blue-900/10 blur-[150px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-indigo-900/10 blur-[150px]" />
      </div>

      <main className="relative z-10 mx-auto max-w-7xl pt-20">
        <Hero onPrimaryClick={() => document.getElementById('quote').scrollIntoView({ behavior: 'smooth' })} />
        
        <div className="section-padding">
          <Problem />
        </div>

        <div className="section-padding">
          <USP />
        </div>

        <div className="section-padding">
          <SamplePages onPreview={setActiveDemo} />
        </div>

        <div id="stories" className="section-padding">
          <SuccessStories />
        </div>

        <div id="reviews" className="section-padding">
          <Reviews />
        </div>

        <div className="section-padding">
          <Process />
        </div>

        <div className="section-padding">
          <Coverage />
        </div>

        <div id="faq" className="section-padding">
          <FAQ />
        </div>

        <div className="section-padding">
          <QuoteForm />
        </div>

        <footer className="py-20 px-6 border-t border-white/5">
          <div className="grid md:grid-cols-3 gap-16 md:gap-8 items-start">
            <div className="text-left">
              <div className="text-white font-black text-xl tracking-tighter mb-2">{CONFIG.PRODUCT_NAME.toUpperCase()}</div>
              <p className="text-slate-500 text-xs font-bold tracking-widest uppercase mb-6">{CONFIG.BRAND_NAME}</p>
              <p className="text-slate-500 text-sm max-w-xs leading-relaxed font-medium">
                Premium digital positioning for Australian professional practices and local businesses. High-performance authority architecture.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-white mb-6">Navigation</p>
                <div className="flex flex-col gap-4 text-xs font-bold text-slate-500">
                  <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-blue-400 transition-colors text-left">Home</button>
                  <a href="#samples" className="hover:text-blue-400 transition-colors">Demo Previews</a>
                  <a href="#stories" className="hover:text-blue-400 transition-colors">Stories</a>
                  <a href="#quote" className="hover:text-blue-400 transition-colors">Quote</a>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-white mb-6">Legal</p>
                <div className="flex flex-col gap-4 text-xs font-bold text-slate-500">
                  <a href="#" className="hover:text-blue-400 transition-colors">Privacy</a>
                  <a href="#" className="hover:text-blue-400 transition-colors">Terms</a>
                </div>
              </div>
            </div>

            <div className="text-left">
              <p className="text-[10px] font-black uppercase tracking-widest text-white mb-6">Contact Details</p>
              <div className="space-y-4">
                <a href={`tel:${CONFIG.SUPPORT_PHONE}`} className="block text-sm font-bold text-slate-300 hover:text-blue-400 transition-colors">
                  {CONFIG.SUPPORT_PHONE}
                </a>
                <a href={`mailto:${CONFIG.SUPPORT_EMAIL}`} className="block text-sm font-bold text-slate-300 hover:text-blue-400 transition-colors">
                  {CONFIG.SUPPORT_EMAIL}
                </a>
                <div className="pt-2">
                  <p className="text-sm text-slate-400 font-bold mb-1">Sydney HQ</p>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {CONFIG.BUSINESS_ADDRESS}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-20 pt-10 border-t border-white/5 text-center">
            <p className="text-slate-600 text-[10px] uppercase tracking-[0.4em] font-black">
              &copy; {new Date().getFullYear()} {CONFIG.BRAND_NAME} &bull; MADE IN AUSTRALIA
            </p>
          </div>
        </footer>
      </main>

      <DemoPreviewModal demo={activeDemo} onClose={() => setActiveDemo(null)} />
    </div>
  );
}

export default App;